import type { SupabaseClient } from '@supabase/supabase-js';

export interface GmailSignaturePreview {
  status: 'preview';
  targetUserId: string;
  email: string;
  html: string;
  warnings: string[];
  previewVersion: string;
  replacesExisting: boolean;
}

export interface GmailSignatureApplied {
  status: 'applied';
  email: string;
  html: string;
}

type SignatureRequest =
  | { action: 'preview'; targetUserId: string }
  | { action: 'apply'; targetUserId: string; previewVersion: string };

export async function configureGmailSignature(
  client: Pick<SupabaseClient, 'functions'>,
  request: SignatureRequest
): Promise<GmailSignaturePreview | GmailSignatureApplied> {
  const { data, error } = await client.functions.invoke('workspace-gmail-signature', { body: request });
  if (error) {
    const response = (error as { context?: unknown }).context;
    let message = 'Could not reach Gmail signature configuration. Try again or contact your administrator.';
    if (response instanceof Response) {
      const payload: unknown = await response.json().catch(() => null);
      if (payload && typeof payload === 'object' && 'message' in payload && typeof payload.message === 'string') {
        message = payload.message;
      }
    }
    throw new Error(message);
  }
  if (!data || typeof data.email !== 'string' || typeof data.html !== 'string' || !data.html.trim()
    || (request.action === 'apply' && data.status !== 'applied')
    || (request.action === 'preview' && (data.status !== 'preview' || data.targetUserId !== request.targetUserId
      || typeof data.previewVersion !== 'string' || !/^[a-f0-9]{64}$/.test(data.previewVersion)
      || typeof data.replacesExisting !== 'boolean' || !Array.isArray(data.warnings)
      || !data.warnings.every((warning: unknown) => typeof warning === 'string')))) {
    throw new Error('Gmail signature configuration returned an incomplete response. Refresh the preview.');
  }
  return data;
}

// Isolate email HTML from the portal. This document has no scripts, forms,
// embeds, network connections, or permission to navigate the parent window.
export function signaturePreviewDocument(html: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="referrer" content="no-referrer"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https://tpwubhqakasyvijikbmv.supabase.co/storage/v1/object/public/template-headers/email-signature/v1/; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'"></head><body style="margin:16px;background:#fff">${html}</body></html>`;
}
